export enum LegalClassType {
    LegalClassA = 1,
    LegalClassB = 2,
    LegalClassC = 3,
    LegalClassD = 4,
    LegalClassE = 5,
}

// WHY GET /products/{id} answered not-available (#410, API RestrictionReasonType). The
// not-available view keys on this — never on whether a destination field is present.
export enum RestrictionReason {
    None = 0,
    // An in-force illegal row for the resolved destination applies to one of the product's
    // legal classes: state, zipCode and restrictionId name the destination and the row.
    DestinationRestriction = 1,
    // The caller has no destination at all (an organisation member seated on no location) and
    // the product carries a legal class, so it fails closed: state, zipCode and restrictionId
    // are null.
    NoDestination = 2,
}

// Why a product cannot ship (#228 / E4-S12, reshaped by #410 and #411). Absent or null when it
// may ship, or when an admin browses with no destination. When it is PRESENT, GET /products/{id}
// answered 200 with `vendors`, `cost`, `price`, `msrp`, `pricePerPiece`, `isAvailable` and
// `hasOffers` blanked on the product AND on every variant — so a restricted product is not a
// missing one, but nothing derived from those fields may reach the screen: a rendered $0.00
// reads as free.
//
// `legalClass` is null for a legal-class lookup row this build's enum does not know (#411 — the
// product then fails closed for every caller); `legalClassName` is always set. Neither it, nor
// `legalClass`, nor `restrictionId` is ever shown to a shopper (Lead ruling 7); they reach the
// DOM only as support-facing data attributes.
export interface ProductRestriction {
    isRestricted: boolean;
    reason: RestrictionReason;
    state: string | null;
    zipCode: string | null;
    legalClass: LegalClassType | null;
    legalClassName: string;
    restrictionId: string | null;
}

// The one place a legal class gets its name. Both surfaces that name a class —
// the product form and the legal-class restriction screen — read from here, so
// renaming a class or adding a sixth cannot make them disagree.
//
// A Record, not an array literal: the compiler flags a new enum member as a
// missing key. LegalClassType.None (0) is deliberately absent from the enum and
// from this map — both restriction validators reject it, so no picker built
// from these entries can offer it.
export const LEGAL_CLASS_LABEL: Record<LegalClassType, string> = {
    [LegalClassType.LegalClassA]: "Legal Class A",
    [LegalClassType.LegalClassB]: "Legal Class B",
    [LegalClassType.LegalClassC]: "Legal Class C",
    [LegalClassType.LegalClassD]: "Legal Class D",
    [LegalClassType.LegalClassE]: "Legal Class E",
};

export const LEGAL_CLASS_OPTIONS = Object.entries(LEGAL_CLASS_LABEL).map(
    ([value, label]) => ({ value, label }),
);

/**
 * A legal class's display name, or a named placeholder when this build has no
 * name for it.
 *
 * The map covers the five classes the validators accept, but that constrains
 * WRITES — not a GET payload. A legacy row carrying None (0), or a class added
 * server-side ahead of this enum, would otherwise render the literal string
 * "undefined", including in a hard-delete confirmation for a compliance rule
 * the screen cannot name. TypeScript cannot catch it: the enum omits the very
 * values that reach here, so the Record is considered total.
 */
export const legalClassLabel = (value: LegalClassType): string => {
    const label: string | undefined = LEGAL_CLASS_LABEL[value];
    return label ?? `Unknown legal class (${value})`;
};

export interface VariantOptionValue {
    id: number;
    value: string;
    isActive: boolean;
    optionId: number;
    optionName: string;
}

export interface Product {
    id: string;
    // The BASE sku's id. Null for a product made of variants, which has no base
    // sku (DecisionLog §3.1, BR-04): each variant carries its own.
    sku?: string | null;
    categoryId: number;
    categoryName: string;
    name: string;
    description: string;
    serialized: boolean;
    upc?: string;
    ean?: string;
    manufacturerSku?: string;
    brand?: string;
    // The BASE sku's sub-label — the size/format line shown under the product
    // title (e.g. "20mg Brownies", "28gram (1oz)"). The API carries it on both
    // the list and the detail read; the UI renders it on the detail page ONLY,
    // by decision — the shared ProductCard also serves the Popular / Related /
    // Featured rails, whose payloads lack the field, so a label on the grid
    // would vanish on the rails beside it. Optional AND nullable: the API sends
    // null when the sku has no label, and payloads predating E4-S8 omit the
    // key entirely.
    displayLabel?: string | null;
    vendors: ProductVendor[];
    cost: number;
    price: number;
    msrp: number;
    // Optional: older /products payloads (pre E3-S7) may omit these. The
    // effective pieces per unit and the derived price / piecesPerUnit.
    piecesPerUnit?: number;
    pricePerPiece?: number;
    discountAllowed: boolean;
    stockQuantity: number;
    isActive: boolean;
    // True iff any active vendor sku with quantity > 0 backs the product
    // (Bug #302). Optional: pre-deploy payloads omit it — treat absent as
    // unavailable.
    isAvailable?: boolean;
    // False when no live vendor offer prices the product (P1, rulings
    // 2026-10-29): `price`, `cost` and `pricePerPiece` are then 0 and mean "no
    // price": the storefront says "Out of stock" and can't order it (PO ruling
    // 2026-11-02), the admin "Price unavailable", never $0.00. Optional
    // only for fixtures that predate it; the API always sends it. Only
    // `false` means unpriced.
    hasOffers?: boolean;
    // Nullable: lower = pinned higher; null = not pinned (E4-S4).
    popularRank?: number | null;
    tags: string[];
    legalClasses: LegalClassType[];
    variants: ProductVariant[];
    images: ProductImage[];
    // Null/absent when the product may ship to the resolved destination — see
    // ProductRestriction. Only GET /products/{id} ever sets it; the list reads simply omit
    // restricted products.
    restriction?: ProductRestriction | null;
    // AC-09: the stamp the product had when this read was taken, sent back on
    // PUT /products/{id} so a save over another admin's newer save is refused
    // (409). Only GET /products/{id} carries it; optional for the list reads,
    // pre-AC-09 payloads and fixtures that predate it.
    concurrencyStamp?: string;
    // #476 (DecisionLog I3): the product's Certificate of Authenticity, or
    // null. Only GET /products/{id} carries it, and never a URL: the file is
    // read through GET /products/{id}/coa. Optional for the list reads and
    // payloads that predate it, so read it as `product.coa ?? null`.
    coa?: ProductCoa | null;
}

// Mirrors the API's GetProductCoaResponse. `updatedAt` is when the COA was
// last written: its replacement, or its first upload.
export interface ProductCoa {
    fileName: string;
    contentType: string;
    updatedAt: string;
}

// Minimal product shape the browse cards/carousels render. Both the full
// `Product` and the lighter `PopularProduct` satisfy it, so a single card
// component serves the product grid and the popular carousels. The piece
// fields and isAvailable are optional, so payloads without them (e.g.
// PopularProduct) still fit.
export type ProductCardData = Pick<
    Product,
    | "id"
    | "name"
    | "price"
    | "images"
    | "piecesPerUnit"
    | "pricePerPiece"
    | "isAvailable"
    | "hasOffers"
>;

// Mirrors a GET /products/popular (E4-S4) response item 1:1. The card only
// reads id/name/price/images (see ProductCardData); `description` is carried to
// match the payload so the DTO stays an accurate mirror of the endpoint.
export interface PopularProduct {
    id: string;
    name: string;
    description: string;
    price: number;
    // As Product.hasOffers.
    hasOffers?: boolean;
    images: ProductImage[];
}

// Mirrors a GET /categories/{id}/featured-product (E4-S5, #183) response 1:1 —
// the SINGLE product one category pins. Satisfies FeaturedBannerProduct (and
// getProductImage's Pick<Product, "images">).
// Not to be confused with FeaturedListProduct below: that one is the
// /products/featured LIST item and carries pricing. Two endpoints, two shapes.
export interface FeaturedProduct {
    id: string;
    name: string;
    brand: string;
    images: { id: string; url: string }[];
}

// Minimal product shape the subcategory featured banner renders. Both the
// endpoint's FeaturedProduct and a full Product (the first-product fallback)
// satisfy it, so the banner takes either without a cast.
export type FeaturedBannerProduct = Pick<
    Product,
    "id" | "name" | "brand" | "images"
>;

// Mirrors a GET /products/featured (#401) response item 1:1 — the cross-category
// list behind the browse home's Featured rail and /c/browse/featured, one entry
// per category that pins a product (#183), active only, ordered by category
// name. Satisfies ProductCardData (piecesPerUnit is optional there, absent here).
// Named for the list because FeaturedProduct above is the single-category read.
export interface FeaturedListProduct {
    id: string;
    name: string;
    brand: string;
    price: number;
    pricePerPiece: number;
    // As Product.hasOffers.
    hasOffers?: boolean;
    images: ProductImage[];
    categoryId: number;
    categoryName: string;
}

// Mirrors a GET /products/related (#449) response item, the same item
// GET /products/{id}/related (E4-S7) returns. `score` is the relatedness
// weight, summed by the API across the cart's products to rank the cart's
// "Related Items" rail. Satisfies ProductCardData (piece fields are optional and
// absent on this lean payload — the card degrades to unit price only).
export interface RelatedProduct {
    id: string;
    name: string;
    description: string;
    price: number;
    // As Product.hasOffers.
    hasOffers?: boolean;
    images: ProductImage[];
    score: number;
}

export interface ProductVariant {
    id: string;
    sku?: string;
    name?: string;
    description?: string;
    // THIS variant sku's own sub-label, same shape as Product.displayLabel.
    // Deliberately not inherited from the product when absent: the label names
    // the sku's size/format, so a base label rendered under a selected variant
    // would misdescribe what goes in the cart.
    displayLabel?: string | null;
    optionValues: VariantOptionValue[];
    // Optional, same as Product: absent on pre-Bug-#302 payloads.
    vendors?: ProductVendor[];
    cost: number;
    price: number;
    msrp: number;
    // Optional, same as Product: per-variant effective pieces per unit and the
    // derived price / piecesPerUnit. May be absent on older payloads.
    piecesPerUnit?: number;
    pricePerPiece?: number;
    stockQuantity: number;
    isActive: boolean;
    // True iff any active vendor sku with quantity > 0 backs the variant
    // (Bug #302). Optional: pre-deploy payloads omit it — treat absent as
    // unavailable.
    isAvailable?: boolean;
    // As Product.hasOffers, for THIS variant's own offers.
    hasOffers?: boolean;
}

export interface ProductImage {
    id: string;
    url: string;
    // E3-S4 (#170): getProduct returns images ordered primary-first by
    // sortOrder. Optional so customer-facing code and existing fixtures (which
    // only read `url`) need no change; the admin image manager reads them.
    isPrimary?: boolean;
    sortOrder?: number;
}

// Mirrors the API's VendorSkuResponse (serialized names verified against the
// backend): `id` is the VendorSkuId the cart API requires per line, and the
// money field on the wire is `price` (there is no `cost`). `isActive` /
// `inStock` are optional because pre-Bug-#302 payloads omit them; note the
// list is NOT filtered server-side — soft-deleted entries appear with
// isActive: false.
export interface ProductVendor {
    id: string;
    vendorId: number;
    vendorName: string;
    price: number;
    isActive?: boolean;
    inStock?: boolean;
    // Which sku this supply row belongs to (#390): the variant's id, or null
    // for the base (variant-less) sku. The discriminator is the VARIANT id and
    // not the sku id on purpose — `sku` is a Guid.ToString() in the projection
    // and so is cased differently under SQLite than under SQL Server, whereas
    // variantId is Guid-to-Guid on both sides and is the value the storefront
    // already holds. Always on the wire (null for a base row); optional in the
    // type only for fixtures that predate it — vendorRowsForSku treats an
    // omitted key as a BASE row (null), not as a row to drop.
    variantId?: string | null;
    // How many this vendor can supply (#443 / C-07, GET /products/{id} only).
    // Add to cart, the vendor switch and checkout refuse a line above it.
    // Optional because the list reads and pre-#443 payloads omit it: absent means
    // unknown, and the storefront then shows no limit (the API still enforces it).
    availableQuantity?: number;
}

export interface GetAllProductsResponse {
    pageNumber: number;
    totalPages: number;
    // Rows matching the filter.
    count: number;
    // TODO(backend): allCount, the rows before any filter (PO ruling X4). Until
    // the API sends it the admin inventory total reads "{count} results".
    allCount?: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    items: Product[];
}

// Mirrors the API's CreateProductCommandBody (multipart) — every field here is
// one the binder reads, under the same name. It has no stock (DecisionLog §1.3:
// stock comes from the vendor sync) and no vendors (vendor offers are
// VendorSkus, written by their own endpoint). The API rejects a blank Name,
// Description, Upc or Brand; the admin form requires those before it calls
// create. Ean and ManufacturerSku are optional (a missing one is stored "").
// No Price or Cost (P1/P2, rulings 2026-10-29): both are derived from the
// vendor offers.
export interface CreateProductRequest {
    categoryId: number;
    name?: string;
    description?: string;
    serialized: boolean;
    upc?: string;
    ean?: string;
    manufacturerSku?: string;
    brand?: string;
    msrp: number;
    discountAllowed: boolean;
    legalClasses?: LegalClassType[];
    tags?: string[];
    variants?: ProductVariantRequest[];
    images?: File[];
    // #476: the optional COA, sent as Body.Coa.
    coa?: File;
}

// Mirrors the API's VariantCommandBody. `options` are option-VALUE ids — the
// API names the list Options. No Price or Cost (P1/P2), as on the product.
export interface ProductVariantRequest {
    name?: string;
    description?: string;
    options?: number[];
    msrp: number;
}

export interface CreateProductResponse {
    id: string;
}

// Mirrors the API's UpdateProductCommandBody (E3-S1 #167). All fields are
// required by the backend; the endpoint does NOT accept Price, Cost (both
// derived from the vendor offers, P1/P2), Vendors, or StockQuantity.
// ProductId is supplied separately as the route param.
// The body's optional PiecesPerUnit is left out on purpose (AC-16): the UI
// has no value of its own to send, only the base sku's from GetProduct, and
// an omitted value keeps the stored one.
export interface UpdateProductRequest {
    categoryId: number;
    name: string;
    description: string;
    upc: string;
    ean: string;
    manufacturerSku: string;
    brand: string;
    msrp: number;
    discountAllowed: boolean;
    isActive: boolean;
    // Nullable: lower = pinned higher; null = not pinned (E4-S4).
    popularRank?: number | null;
    legalClasses: LegalClassType[];
    tags: string[];
    // AC-09: the stamp of the read the form was filled from. A stale one is
    // refused with 409; a missing one is not checked (until the API requires
    // it), so the edit page always sends what its read returned.
    concurrencyStamp?: string;
}
